import classNames from 'classnames';
import _ from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useNavigate } from 'react-router-dom';
import tinycolor from 'tinycolor2';
import { useOrderAPI } from '../../apis/order-api';
import { useStorage } from '../../hooks/use-storage';
import { useErrorModal } from '../../screens/error-modal';
import { layoutDefaults } from '../../utils/layout-defaults';
import {
  filterByUser,
  parseUserTotal,
} from '../../utils/manager/order-manager';
import { parseValue } from '../../utils/manager/price-manager';
import { useSpotManager } from '../../utils/manager/spot-manager';

const buttonAnimationEnter =
  'animate__animated animate__fadeInUp animate__faster';

const buttonAttentionAnimationEnter =
  'animate__animated animate__bounceInUp animate__normal';

const buttonAnimationExit =
  'animate__animated animate__fadeOutDown animate__faster';

function AccountButton({ setUpdating }) {
  const [loading, setLoading] = useState(false);
  const {
    menuSlug,
    userRef,
    spotRef,
    ordersRef,
    setOrders,
    setSpot,
    menuInfo,
    menuMetadata,
    showingModal,
    filterCount,
  } = useStorage();
  const { showErrorModal } = useErrorModal();
  const { findSpotOrdersFromRemote } = useOrderAPI();
  const classes = useStyles(menuMetadata?.layout);
  const [animationClass, setAnimationClass] = useState(null);
  const { checkSpotChange } = useSpotManager();
  const navigate = useNavigate();
  const timeoutRef = useRef();

  const totalValue = useMemo(
    () => parseUserTotal(ordersRef.current, userRef.current),
    [ordersRef.current, userRef]
  );

  const hasPrepaidOrderToSend = useMemo(
    () =>
      !_.isEmpty(
        filterByUser(ordersRef.current, userRef.current)?.filter(
          (o) => !o.session?.expires_at
        )
      ),
    [ordersRef.current, userRef.current]
  );

  const { t } = useTranslation();

  useEffect(() => {
    clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      let lAnimationClass = buttonAnimationEnter;

      if (showingModal || filterCount === 0) {
        lAnimationClass = buttonAnimationExit;
      } else if (totalValue > 0) {
        lAnimationClass = buttonAttentionAnimationEnter;
      } else {
        lAnimationClass = buttonAnimationEnter;
      }

      // showingModal
      // ? buttonAnimationExit
      // : totalValue > 0
      // ? buttonAttentionAnimationEnter
      // : buttonAnimationEnter

      setAnimationClass(lAnimationClass);
    }, 400);
  }, [showingModal, totalValue, filterCount]);

  const loadBill = async () => {
    if (loading) return;

    setLoading(true);
    setUpdating(false);
    let shouldNavigate = true;

    try {
      const response = await findSpotOrdersFromRemote();
      const rOrders = response.data;
      await checkSpotChange(rOrders);

      setOrders(rOrders);
    } catch (error) {
      if (error.response?.status === 401) {
        setOrders(null);
      } else if (
        error.response?.status === 422 &&
        error.response?.data?.find((v) => v.code === 1) != null
      ) {
        setSpot(null);
        setOrders(null);
      } else {
        shouldNavigate = false;
        await showErrorModal(error);
      }
    }

    setLoading(false);
    setUpdating(true);

    if (shouldNavigate) showBill();
  };

  function showBill() {
    navigate(`/${menuSlug}/conta`);
  }

  const title = useMemo(() => {
    if (spotRef.current?.prepaid && hasPrepaidOrderToSend) {
      return `${
        totalValue > 0
          ? 'account-button.send-order'
          : 'account-button.order-and-payment'
      }`;
    }

    return `${
      totalValue > 0
        ? 'account-button.bill-and-payment'
        : 'account-button.order-and-payment'
    }`;
  }, [totalValue, hasPrepaidOrderToSend, spotRef.current]);

  return (
    animationClass != null && (
      <Button
        className={classNames(classes.container, animationClass)}
        onClick={loadBill}
        variant="primary"
        size="md"
      >
        {loading ? <Spinner animation="border" /> : <span>{t(title)}</span>}
        {totalValue > 0 && (
          <span className={classes.totalContainer}>
            <span className={classes.total}>
              {menuInfo.i18n?.currency?.symbol} {parseValue(totalValue)}
            </span>
          </span>
        )}
      </Button>
    )
  );
}

const useStyles = createUseStyles({
  container: {
    boxShadow: `0px 2px 5px #22222266`,
    fontSize: layoutDefaults.fontSize,
    fontFamily: (layout) =>
      layout?.text.default_font ?? layoutDefaults.fontFamily,

    // marginLeft: layoutDefaults.horizontalMargin + 2,
    // marginRight: layoutDefaults.horizontalMargin + 2,

    // marginLeft: layoutDefaults.smallerHorizontalMargin,
    // marginRight: layoutDefaults.smallerHorizontalMargin,
    // border: '1px solid white',
  },
  totalContainer: {
    position: 'absolute',
    top: 9,
    right: 8,
  },
  total: {
    color: 'white',
    fontSize: layoutDefaults.smallFontSize,
    opacity: 0.75,
    backgroundColor: tinycolor(layoutDefaults.secondaryColor)
      .darken(10)
      .toString(),
    padding: 6,
    borderRadius: layoutDefaults.radius,
    // verticalAlign: "center",
  },
});

export { AccountButton };
